/**
 * The Control Panel's map frame, shared by list pages with a map (Hardware,
 * Projects): a "Map View" bar whose map icon slides the map open or closed,
 * and a drag handle underneath to resize it. On mobile the open/close
 * control lives in the bottom nav, so only a hairline seam is drawn (or
 * nothing, with showSeam false: the phone Control Panel).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Collapse, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import {
  Map as MapIcon,
  MapOutlined as MapOutlinedIcon,
  DragHandle as DragHandleIcon,
} from '@mui/icons-material';
import { useMap } from 'react-leaflet';

const MIN_HEIGHT = 150;
const MAX_HEIGHT = 800;

/** Put inside a MapContainer: re-measures the map after it opens or is resized. */
export const InvalidateMapSize: React.FC<{ height: number; open: boolean }> = ({ height, open }) => {
  const map = useMap();
  useEffect(() => {
    const timeout = setTimeout(() => {
      if ((map as any)._mapPane) map.invalidateSize();
    }, 300);
    return () => clearTimeout(timeout);
  }, [map, height, open]);
  return null;
};

interface CollapsibleMapPanelProps {
  isMobile: boolean;
  open: boolean;
  onToggle: () => void;
  defaultHeight?: number;
  mobileHeight?: number;
  /** On mobile, draw the hairline seam the map slides out of (default
   *  true). Without it a closed map takes no space at all. */
  showSeam?: boolean;
  /** Renders the map at the current height. */
  children: (height: number) => React.ReactNode;
}

const CollapsibleMapPanel: React.FC<CollapsibleMapPanelProps> = ({
  isMobile, open, onToggle, defaultHeight = 300, mobileHeight = 200, showSeam = true, children,
}) => {
  const { t } = useTranslation();
  const [height, setHeight] = useState(defaultHeight);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(0);
  const effectiveHeight = isMobile ? mobileHeight : height;

  const handleDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartY.current = e.clientY;
    dragStartHeight.current = height;
    const onMove = (ev: MouseEvent) => {
      const next = dragStartHeight.current + (ev.clientY - dragStartY.current);
      setHeight(Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, next)));
    };
    const onUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <Paper
      elevation={isMobile && !open ? 0 : 1}
      sx={{ mb: isMobile && !showSeam && !open ? 0 : 2, overflow: 'hidden', borderRadius: 1 }}
    >
      {isMobile ? (
        /* Faint hairline seam the map slides out of; purely visual. */
        showSeam && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ width: 32, height: 3, borderRadius: 2, bgcolor: 'divider' }} />
          </Box>
        )
      ) : (
        <Box
          display="flex" alignItems="center" px={1.5} py={0.5}
          sx={{ borderBottom: open ? 1 : 'none', borderBottomColor: 'grey.300', backgroundColor: 'grey.50' }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={open ? t('dashboard.hideMap') : t('dashboard.showMap')}>
              <IconButton size="small" onClick={onToggle} color="primary">
                {open ? <MapIcon fontSize="small" /> : <MapOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {t('dashboard.mapView')}
            </Typography>
          </Box>
        </Box>
      )}
      <Collapse in={open}>
        <Box sx={{
          height: effectiveHeight,
          position: 'relative',
          transition: isDragging ? 'none' : 'height 0.3s ease',
          '& .leaflet-control-attribution': { display: 'none' },
        }}>
          {children(effectiveHeight)}
        </Box>
        {!isMobile && (
          <Box
            onMouseDown={handleDragStart}
            sx={{
              height: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'row-resize', backgroundColor: 'grey.50',
              borderTop: 1, borderTopColor: 'grey.300',
              '&:hover': { backgroundColor: 'grey.100' }, userSelect: 'none',
            }}
          >
            <DragHandleIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

export default CollapsibleMapPanel;
